<template>
  <svg
    class="promotion-growth-icon"
    :class="`promotion-growth-icon--${size}`"
    viewBox="0 0 72 48"
    preserveAspectRatio="xMidYMax meet"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <linearGradient
        :id="barGradientId"
        x1="36"
        y1="48"
        x2="36"
        y2="17"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0%" :stop-color="lightColor" />
        <stop offset="100%" :stop-color="color" />
      </linearGradient>
      <linearGradient
        :id="arrowGradientId"
        x1="0"
        y1="8"
        x2="72"
        y2="8"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0%" :stop-color="lightColor" />
        <stop offset="45%" :stop-color="color" />
        <stop offset="100%" :stop-color="color" />
      </linearGradient>
    </defs>

    <rect
      v-for="(bar, index) in bars"
      :key="index"
      :x="bar.x"
      :y="bar.y"
      :width="bar.width"
      :height="bar.height"
      rx="2"
      :fill="`url(#${barGradientId})`"
    />

    <path
      :d="arrowLinePath"
      :stroke="`url(#${arrowGradientId})`"
      stroke-width="2.75"
      stroke-linecap="butt"
      stroke-linejoin="miter"
      stroke-miterlimit="10"
      shape-rendering="geometricPrecision"
      fill="none"
    />
    <path :d="arrowHeadPath" :fill="`url(#${arrowGradientId})`" />
  </svg>
</template>

<script>
const CHART_BOTTOM = 48;
const CHART_TOP = 17;
const CHART_HEIGHT = CHART_BOTTOM - CHART_TOP;
const BAR_COUNT = 6;
const BAR_GAP = 3;
const VIEW_WIDTH = 72;
const BAR_WIDTH = (VIEW_WIDTH - BAR_GAP * (BAR_COUNT - 1)) / BAR_COUNT;

const BARS = [0.32, 0.32, 0.32, 0.48, 0.72, 1].map((ratio, index) => {
  const height = Math.max(ratio * CHART_HEIGHT, 4);

  return {
    x: index * (BAR_WIDTH + BAR_GAP),
    y: CHART_BOTTOM - height,
    width: BAR_WIDTH,
    height,
  };
});

function svgPoint(x, y) {
  const round = (value) => Math.round(value * 100) / 100;

  return `${round(x)} ${round(y)}`;
}

const BEND_Y = 14;
const BEND_X = 35;
const TIP = { x: 60, y: 1.5 };
const HEAD_LEN = 7.5;
const HEAD_HALF = 4;

const dx = TIP.x - BEND_X;
const dy = TIP.y - BEND_Y;
const len = Math.hypot(dx, dy) || 1;
const dirX = dx / len;
const dirY = dy / len;
const baseX = TIP.x - dirX * HEAD_LEN;
const baseY = TIP.y - dirY * HEAD_LEN;
const shaftX = baseX + dirX * 0.75;
const shaftY = baseY + dirY * 0.75;
const perpX = -dirY;
const perpY = dirX;

const ARROW_LINE_PATH = `M 0 ${BEND_Y} H ${BEND_X} L ${svgPoint(shaftX, shaftY)}`;
const ARROW_HEAD_PATH = [
  `M ${svgPoint(TIP.x, TIP.y)}`,
  `L ${svgPoint(baseX + perpX * HEAD_HALF, baseY + perpY * HEAD_HALF)}`,
  `L ${svgPoint(baseX - perpX * HEAD_HALF, baseY - perpY * HEAD_HALF)}`,
  "Z",
].join(" ");

function lightenHex(hex, amount) {
  let normalized = hex.replace("#", "");

  if (normalized.length === 3) {
    normalized = normalized
      .split("")
      .map((char) => char + char)
      .join("");
  }

  if (normalized.length !== 6) {
    return hex;
  }

  const mix = (offset) => {
    const channel = parseInt(normalized.slice(offset, offset + 2), 16);

    return Math.round(channel + (255 - channel) * amount);
  };

  return `rgb(${mix(0)}, ${mix(2)}, ${mix(4)})`;
}

export default {
  name: "PromotionGrowthIcon",

  props: {
    color: {
      type: String,
      default: "#3b82f6",
    },

    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md"].includes(value),
    },
  },

  data() {
    return {
      bars: BARS,
      arrowLinePath: ARROW_LINE_PATH,
      arrowHeadPath: ARROW_HEAD_PATH,
    };
  },

  computed: {
    lightColor() {
      return lightenHex(this.color, 0.45);
    },

    barGradientId() {
      return `promotion-growth-bars-${this._uid}`;
    },

    arrowGradientId() {
      return `promotion-growth-arrow-${this._uid}`;
    },
  },
};
</script>

<style scoped lang="postcss">
.promotion-growth-icon {
  @apply shrink-0;
  aspect-ratio: 72 / 48;
  width: 4.5rem;
  height: auto;
}

.promotion-growth-icon--sm {
  width: 3.25rem;
}
</style>
